import React, { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Common } from "../../../utils/common";
import { createAdditionalProfileValidationSchema } from "./userFormAdditionalProfileValidation";
import {
  createUserFormValidationSchema,
  getUserFormFieldSections,
  getUserFormValidationIssues,
  scrollToFormField,
} from "./userFormValidationRules";

function getNestedTouched(touched, path) {
  if (!path) return false;
  const value = path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), touched);
  return Boolean(value);
}

const GUIDE_TOP_OFFSET = 16;
const DESKTOP_SCROLL_TRACK_MEDIA = "(min-width: 1201px)";

function clearScrollFollowStyles(el) {
  el.style.position = "";
  el.style.top = "";
  el.style.left = "";
  el.style.right = "";
}

export default function UserFormValidationGuide({
  values,
  errors,
  touched,
  submitCount,
  setFieldTouched,
  mode,
  isAdminInEditMode,
  formReady = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const guideRef = useRef(null);
  const panelRef = useRef(null);
  const [scrollTracking, setScrollTracking] = useState(false);
  const [formInView, setFormInView] = useState(false);
  const [attentionPulse, setAttentionPulse] = useState(false);
  const lastHandledSubmitRef = useRef(0);

  const PANEL_BOTTOM_RESERVE = 76;

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_SCROLL_TRACK_MEDIA);
    const sync = () => setScrollTracking(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!formReady) {
      setFormInView(false);
      return undefined;
    }

    const wrap = document.querySelector(".user-form-with-guide");
    if (!wrap) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setFormInView(entry.isIntersecting),
      { threshold: 0.08, rootMargin: "0px 0px -10% 0px" }
    );

    observer.observe(wrap);
    return () => observer.disconnect();
  }, [formReady]);

  useEffect(() => {
    if (!formReady || !formInView) {
      setOpen(false);
    }
  }, [formReady, formInView]);

  useEffect(() => {
    if (!scrollTracking || !formReady || !formInView) {
      const el = guideRef.current;
      if (el) clearScrollFollowStyles(el);
      return undefined;
    }

    const updateGuidePosition = () => {
      const el = guideRef.current;
      const wrap = el?.closest(".user-form-with-guide");
      if (!el || !wrap) return;

      const wrapRect = wrap.getBoundingClientRect();
      const guideHeight = el.getBoundingClientRect().height || 56;
      const guideWidth = 56;

      let top = GUIDE_TOP_OFFSET;
      if (wrapRect.top > GUIDE_TOP_OFFSET) {
        top = wrapRect.top;
      } else if (wrapRect.bottom - guideHeight < GUIDE_TOP_OFFSET) {
        top = Math.max(GUIDE_TOP_OFFSET, wrapRect.bottom - guideHeight);
      }

      const left = Math.min(
        Math.round(wrapRect.right),
        window.innerWidth - guideWidth - 8
      );

      el.style.position = "fixed";
      el.style.top = `${top}px`;
      el.style.left = `${Math.max(8, left)}px`;
      el.style.right = "auto";
    };

    updateGuidePosition();
    document.addEventListener("scroll", updateGuidePosition, { passive: true, capture: true });
    window.addEventListener("resize", updateGuidePosition, { passive: true });
    return () => {
      document.removeEventListener("scroll", updateGuidePosition, { capture: true });
      window.removeEventListener("resize", updateGuidePosition);
      const el = guideRef.current;
      if (el) clearScrollFollowStyles(el);
    };
  }, [scrollTracking, open, formReady, formInView]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (guideRef.current && !guideRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open || !panelRef.current) return undefined;

    const panel = panelRef.current;

    const syncPanelHeight = () => {
      const rect = panel.getBoundingClientRect();
      const available = window.innerHeight - Math.max(0, rect.top) - PANEL_BOTTOM_RESERVE;
      panel.style.maxHeight = `${Math.max(160, Math.min(available, 520))}px`;
    };

    syncPanelHeight();
    window.addEventListener("resize", syncPanelHeight);
    document.addEventListener("scroll", syncPanelHeight, { passive: true, capture: true });
    return () => {
      window.removeEventListener("resize", syncPanelHeight);
      document.removeEventListener("scroll", syncPanelHeight, { capture: true });
      panel.style.maxHeight = "";
    };
  }, [open, scrollTracking]);

  const additionalProfileSchema = useMemo(
    () => createAdditionalProfileValidationSchema(t),
    [t]
  );
  const schema = useMemo(
    () => createUserFormValidationSchema(t, mode, additionalProfileSchema),
    [t, mode, additionalProfileSchema]
  );
  const sections = useMemo(
    () => getUserFormFieldSections(t, mode, isAdminInEditMode, values),
    [t, mode, isAdminInEditMode, values]
  );

  const issues = useMemo(
    () =>
      getUserFormValidationIssues(
        values,
        errors,
        touched,
        schema,
        sections,
        additionalProfileSchema,
        { mode, submitCount }
      ),
    [values, errors, touched, schema, sections, additionalProfileSchema, mode, submitCount]
  );

  const issueMap = useMemo(
    () => issues.reduce((acc, item) => ({ ...acc, [item.touchPath]: item.message }), {}),
    [issues]
  );

  const allValid = issues.length === 0;
  const panelStateClass = allValid
    ? "user-form-validation-guide--valid"
    : "user-form-validation-guide--invalid";
  const showLiveValidationHint = mode === "create" && !allValid;

  useEffect(() => {
    if (submitCount <= lastHandledSubmitRef.current) return;

    lastHandledSubmitRef.current = submitCount;
    if (issues.length === 0) return;

    Common.showErrorMessage(
      t("app.users.validationFixBeforeSubmit"),
      t("app.users.validationFixBeforeSubmitTitle")
    );
    setOpen(true);
    setAttentionPulse(true);
    issues.forEach((issue) => setFieldTouched(issue.touchPath, true, false));
    scrollToFormField(issues[0].id);

    const timer = window.setTimeout(() => setAttentionPulse(false), 700);
    return () => window.clearTimeout(timer);
  }, [submitCount, issues, setFieldTouched, t]);

  const handleNavigate = (field) => {
    setFieldTouched(field.touchPath, true, false);
    scrollToFormField(field.id);
  };

  const isFieldTouched = (field) =>
    getNestedTouched(touched, field.touchPath) || submitCount > 0;

  if (!formReady || !formInView) {
    return null;
  }

  return (
    <aside
      ref={guideRef}
      className={`user-form-validation-guide ${panelStateClass} ${open ? "is-open" : ""} ${
        scrollTracking ? "is-scroll-tracking" : ""
      } ${attentionPulse ? "is-attention" : ""} ${showLiveValidationHint ? "is-validating" : ""}`}
    >
      <button
        type="button"
        className={`user-form-validation-guide__toggle ${
          showLiveValidationHint ? "has-validation-issues" : ""
        } ${attentionPulse ? "is-attention" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls="user-form-validation-guide-panel"
        title={t("app.users.validationGuideToggle")}
      >
        <i
          className={`bi user-form-validation-guide__toggle-icon ${
            allValid ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"
          }`}
          aria-hidden="true"
        />
        <span className="user-form-validation-guide__toggle-label">
          {allValid ? t("app.users.validationGuideOkShort") : t("app.users.validationGuideIssuesShort")}
        </span>
      </button>

      {open && (
        <section
          ref={panelRef}
          id="user-form-validation-guide-panel"
          className="user-form-validation-guide__panel"
        >
          <header className="user-form-validation-guide__header">
            <div className="user-form-validation-guide__header-main">
              {allValid ? (
                <>
                  <i className="bi bi-check-circle-fill user-form-validation-guide__header-icon" aria-hidden="true" />
                  <span>
                    <strong className="user-form-validation-guide__title">
                      {t("app.users.validationGuideAllOkTitle")}
                    </strong>
                    <span className="user-form-validation-guide__subtitle">
                      {t("app.users.validationGuideAllOkSubtitleFull")}
                    </span>
                  </span>
                </>
              ) : (
                <>
                  <i className="bi bi-exclamation-triangle-fill user-form-validation-guide__header-icon" aria-hidden="true" />
                  <span>
                    <strong className="user-form-validation-guide__title">
                      {t("app.users.validationGuideIssuesTitle")}
                    </strong>
                    <span className="user-form-validation-guide__subtitle">
                      {t("app.users.validationGuideIssuesSubtitleFull")}
                    </span>
                  </span>
                </>
              )}
            </div>
            <button
              type="button"
              className="user-form-validation-guide__close"
              onClick={() => setOpen(false)}
              aria-label={t("app.users.validationGuideClose")}
              title={t("app.users.validationGuideClose")}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </header>

          <div className="user-form-validation-guide__sections">
            {sections.map((section) => (
              <div key={section.id} className="user-form-validation-guide__section">
                <h6 className="user-form-validation-guide__section-title">{section.title}</h6>
                <ul className="user-form-validation-guide__list">
                  {section.fields.map((field) => {
                    const fieldIssue = issueMap[field.touchPath];
                    const itemValid = !fieldIssue;

                    return (
                      <li key={field.touchPath} className="user-form-validation-guide__item">
                        <button
                          type="button"
                          className={`user-form-validation-guide__item-btn ${
                            fieldIssue ? "has-error" : itemValid ? "is-valid" : ""
                          }`}
                          onClick={() => handleNavigate(field)}
                        >
                          <span className="user-form-validation-guide__item-status" aria-hidden="true">
                            <i
                              className={`bi ${
                                fieldIssue
                                  ? "bi-x-circle-fill text-danger"
                                  : itemValid
                                    ? "bi-check-circle-fill text-success"
                                    : "bi-dot text-muted"
                              }`}
                            />
                          </span>
                          <span className="user-form-validation-guide__item-content">
                            <span className="user-form-validation-guide__item-label">{field.label}</span>
                            <span className="user-form-validation-guide__item-requirement">{field.requirement}</span>
                            {fieldIssue && (
                              <span className="user-form-validation-guide__item-error">{fieldIssue}</span>
                            )}
                            {!fieldIssue && isFieldTouched(field) && (
                              <span className="user-form-validation-guide__item-ok">{t("app.users.validationFieldOk")}</span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}
    </aside>
  );
}
